import React, { useContext } from "react";
import { MainContext } from "../../context/MainContext";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";

import SettingsEthernetOutlinedIcon from "@mui/icons-material/SettingsEthernetOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import DeviceThermostatOutlinedIcon from "@mui/icons-material/DeviceThermostatOutlined";

function makeTile(label, icon, value) {
  const v = value?.toString() ?? "";

  const isAlert =
    v.toLowerCase().includes("disabled") ||
    v.toLowerCase().includes("starting");

  const tileBg = isAlert
    ? "rgba(244, 67, 54, 0.22)"
    : "rgba(255,255,255,0.04)";

  const valueColor = isAlert
    ? "rgba(244,67,54,1)"
    : "rgba(255,255,255,0.7)";

  const iconBg = isAlert
    ? "rgba(244,67,54,0.9)"
    : "rgba(255,255,255,0.12)";

  const iconColor = isAlert ? "#000" : "rgba(255,255,255,0.7)";

  return { label, icon, value, tileBg, valueColor, iconBg, iconColor };
}

export default function HPDashboardEVI() {
  const state = useContext(MainContext).state;
  const re = state.RE.split(",");

  const ICON_COLORS = {
    heat: "#FF7043",        // orange / heat
    cool: "#4FC3F7",        // light blue
    water: "#29B6F6",       // pool / water
    time: "#BA68C8",        // timers
    power: "#FFD54F",       // electrical
    speed: "#81C784",       // motion / speed
    level: "#90A4AE",       // neutral / measurement
    error: "#FF0000",
    bluetooth: "#0a3d91",
    wifi: "#D3D3D3",
    mqtt: "#660066"
  };

  // ---- MODE ----
  let mode = "N/A";
  if (re[45] === "0") mode = "Auto";
  if (re[45] === "1") mode = "Manual";
  if (re[45] === "2") mode = "Disabled";

  // Default fallbacks
  let currentStep = "0";
  let resetState = "0";
  let measuredTemp = "...";
  let targetTemp = "...";

  if (re.length > 31) {
    currentStep = re[47];

    const resetMap = {
      0: "Starting",
      1: "Closing",
      2: "Close pause",
      3: "Start step",
      4: "Start pause",
      5: "",
    };
    resetState = resetMap[Number(re[48])] ?? "N/A";

    measuredTemp = (re[54] / 10.0).toFixed(1) + "°C";
    targetTemp = re[46] + "°C";
  }

  // Build tiles
  const tiles = [
    makeTile("Mode", <SettingsEthernetOutlinedIcon sx={{ color: ICON_COLORS.level }} />, mode),
    makeTile("Current Step", <TimelineOutlinedIcon sx={{ color: ICON_COLORS.water }} />, currentStep),
    makeTile("Reset State", <RestartAltOutlinedIcon />, resetState),
    makeTile("Control Temp (Measured)", <DeviceThermostatOutlinedIcon sx={{ color: ICON_COLORS.cool }} />, measuredTemp),
    makeTile("Control Temp (Target)", <DeviceThermostatOutlinedIcon sx={{ color: ICON_COLORS.cool }} />, targetTemp),
  ];

  return (
    <React.Fragment>
      <Typography
        component="h2"
        variant="h6"
        color="white"
        gutterBottom
        sx={{ fontWeight: 600 }}
      >
        EVI
      </Typography>

      <Grid container spacing={1.5}>
        {tiles.map((item) => (
          <Grid item xs={12} sm={6} md={6} key={item.label}>
            <Paper
              sx={{
                p: 1.2,
                height: 60,
                display: "flex",
                alignItems: "center",
                gap: 1.2,
                borderRadius: 2,
                bgcolor: item.tileBg,
                border: "1px solid rgba(255,255,255,0.1)",
                transition: "0.2s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  bgcolor:
                    item.tileBg === "rgba(244, 67, 54, 0.22)"
                      ? "rgba(244,67,54,0.32)"
                      : "rgba(255,255,255,0.08)",
                },
              }}
            >
              {/* Icon bubble */}
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: item.iconBg,
                  color: item.iconColor,
                  flexShrink: 0,
                }}
              >
                {item.icon}
              </Box>

              {/* Label + Value */}
              <Box sx={{ flexGrow: 1 }}>
                <Typography
                  color="white"
                  sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.1 }}
                >
                  {item.label}
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.disabled',
                    fontSize: 13,
                  }}
                >
                  {item.value}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </React.Fragment>
  );
}
